import { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Upload } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { fmtEuro, SERVICE_OPTIONS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const F = ({ label, children }) => <div><Label className="micro-label">{label}</Label><div className="mt-1.5">{children}</div></div>;
const Pick = ({ value, onChange, options, placeholder, testId }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
  </Select>
);

const Preview = ({ p }) => (
  <div className="space-y-2" data-testid="pl-import-preview">
    <div className="flex flex-wrap gap-4 text-sm">
      <span><b data-testid="pl-import-count">{p.items.length}</b> voci</span>
      <span className={p.duplicates.length ? "font-semibold text-amber-700" : "text-muted-foreground"} data-testid="pl-import-dups">{p.duplicates.length} codici doppi{p.duplicates.length ? ` (scartati: ${p.duplicates.slice(0, 5).join(", ")})` : ""}</span>
      <span className={p.missing_values.length ? "font-semibold text-red-700" : "text-muted-foreground"} data-testid="pl-import-missing">{p.missing_values.length} valori mancanti{p.missing_values.length ? ` (a 0,00 €: ${p.missing_values.slice(0, 5).join(", ")})` : ""}</span>
    </div>
    <div className="max-h-56 overflow-y-auto border text-xs">
      {p.items.map((i) => (
        <div key={i.code} className="grid grid-cols-[140px_1fr_90px] gap-2 border-b px-2 py-1 last:border-b-0">
          <span className="font-mono">{i.code}</span><span className="truncate">{i.description}</span><span className="text-right font-mono">{fmtEuro(i.price)}</span>
        </div>
      ))}
    </div>
  </div>
);

export const PriceListImport = ({ onDone }) => {
  const { parties, companies, projects } = useData();
  const [open, setOpen] = useState(false);
  const [p, setP] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/price-lists/import/preview", fd);
      const g = data.guess;
      const proj = projects.find((x) => x.id === data.project_id);
      const d = new Date();
      setP(data);
      setF({ mode: data.project_id ? "existing" : "new", project_id: data.project_id || "", customer_id: g.customer_id || "", infrastructure_manager_id: g.infrastructure_manager_id || "",
        company_id: g.company_id || "", region: g.region || "", service_types: g.service_types.length ? g.service_types : proj?.service_types || ["DELIVERY"], code: "", name: data.filename.replace(/\.[^.]+$/, ""),
        valid_from: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const body = { service_types: f.service_types, valid_from: f.valid_from, items: p.items,
        ...(f.mode === "existing" ? { project_id: f.project_id } : { new_project: { code: f.code, name: f.name, customer_id: f.customer_id, infrastructure_manager_id: f.infrastructure_manager_id || null, company_id: f.company_id, region: f.region } }) };
      const { data } = await api.post("/price-lists/import", body);
      toast.success(`${data.created.length} listini creati su ${data.project_code} (${data.items} voci)${data.closed_previous ? ` · ${data.closed_previous} versioni precedenti chiuse` : ""}`);
      setOpen(false); setP(null); onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const opt = (l) => l.map((x) => ({ value: x.id, label: x.legal_name || x.name }));
  const ok = p && f && f.service_types.length && f.valid_from && (f.mode === "existing" ? f.project_id : f.code && f.name && f.customer_id && f.company_id);
  return (
    <>
      <Button variant="outline" onClick={() => { setP(null); setOpen(true); }} data-testid="pl-import-open"><FileSpreadsheet className="mr-2 h-4 w-4" />Importa listino Excel</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="pl-import-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Importa listino da Excel</DialogTitle></DialogHeader>
          <label className="flex cursor-pointer items-center gap-2 border border-dashed p-4 text-sm hover:bg-muted/40">
            <Upload className="h-4 w-4" />{p ? p.filename : "Scegli il file (.xlsx): prime 3 colonne = codice voce, descrizione, valore €"}
            <input type="file" accept=".xlsx" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="pl-import-file" />
          </label>
          {p && f && (
            <div className="space-y-4">
              <div className="inline-flex border p-1">
                {[["existing", "Commessa esistente"], ["new", "Nuova commessa"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => set("mode")(k)} className={`px-3 py-1.5 text-sm font-semibold ${f.mode === k ? "bg-foreground text-background" : "hover:bg-muted"}`} data-testid={`pl-import-mode-${k}`}>{l}</button>
                ))}
              </div>
              {f.mode === "existing" ? (
                <F label="Commessa"><Pick value={f.project_id} onChange={set("project_id")} placeholder="Seleziona commessa" testId="pl-import-project" options={projects.map((x) => ({ value: x.id, label: `${x.code} — ${x.name}` }))} /></F>
              ) : (
                <div className="grid gap-3 md:grid-cols-2">
                  <F label="Codice commessa"><Input value={f.code} onChange={(e) => set("code")(e.target.value.toUpperCase())} data-testid="pl-import-code" /></F>
                  <F label="Descrizione"><Input value={f.name} onChange={(e) => set("name")(e.target.value)} data-testid="pl-import-name" /></F>
                  <F label="Committente"><Pick value={f.customer_id} onChange={set("customer_id")} placeholder="Seleziona" testId="pl-import-customer" options={opt(parties.filter((x) => x.roles.includes("customer")))} /></F>
                  <F label="Gestore"><Pick value={f.infrastructure_manager_id} onChange={set("infrastructure_manager_id")} placeholder="Nessuno" testId="pl-import-manager" options={opt(parties.filter((x) => x.roles.includes("infrastructure_manager")))} /></F>
                  <F label="Società titolare"><Pick value={f.company_id} onChange={set("company_id")} placeholder="Seleziona" testId="pl-import-company" options={opt(companies)} /></F>
                  <F label="Regione"><Input value={f.region} onChange={(e) => set("region")(e.target.value)} data-testid="pl-import-region" /></F>
                </div>
              )}
              <div className="flex flex-wrap items-end gap-6">
                <F label="Attività">
                  <div className="flex gap-4">{SERVICE_OPTIONS.map((s) => (
                    <label key={s.value} className="flex items-center gap-1.5 text-sm"><Checkbox checked={f.service_types.includes(s.value)} data-testid={`pl-import-svc-${s.value}`}
                      onCheckedChange={(c) => set("service_types")(c ? [...f.service_types, s.value] : f.service_types.filter((x) => x !== s.value))} />{s.label}</label>
                  ))}</div>
                </F>
                <F label="Valido dal"><Input type="date" value={f.valid_from} onChange={(e) => set("valid_from")(e.target.value)} data-testid="pl-import-from" /></F>
              </div>
              <p className="text-xs text-muted-foreground">Viene creato un listino per ogni attività scelta. Se la commessa ha già un listino per la stessa attività, quello vecchio si chiude il giorno prima della nuova data.</p>
              <Preview p={p} />
            </div>
          )}
          <DialogFooter><Button disabled={!ok || busy} onClick={save} data-testid="pl-import-save">Importa listino</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
